import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Seo from "@/components/site/Seo";
import { fetchProductByHandle, formatMoney } from "@/lib/shopify";
import { useCart } from "@/context/CartContext";
import { beautySalonSchema, breadcrumb } from "@/data/schema";
import {
  ArrowLeft,
  ShoppingBag,
  Check,
  Minus,
  Plus,
  Loader2,
  AlertCircle,
} from "lucide-react";

export default function ProductDetail() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addItem, busy } = useCart();

  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [variantId, setVariantId] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    fetchProductByHandle(handle)
      .then((p) => {
        if (!alive) return;
        if (!p) {
          setStatus("notfound");
          return;
        }
        setProduct(p);
        const firstAvail = p.variants.find((v) => v.availableForSale) || p.variants[0];
        setVariantId(firstAvail?.id);
        setStatus("done");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
    };
  }, [handle]);

  const selectedVariant = useMemo(
    () => product?.variants.find((v) => v.id === variantId) || product?.variants[0],
    [product, variantId]
  );

  if (status === "loading") {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 bg-onyx pt-24">
        <Loader2 size={30} className="animate-spin text-gold" />
        <p className="text-[0.7rem] uppercase tracking-[0.3em] text-muted-ivory">Loading product</p>
      </div>
    );
  }

  if (status === "error" || status === "notfound") {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-onyx px-6 pt-24 text-center">
        <AlertCircle size={44} className="text-gold/60" />
        <p className="text-muted-ivory">
          {status === "notfound"
            ? "We couldn't find that product."
            : "Something went wrong loading this product."}
        </p>
        <Link
          to="/shop"
          className="btn-gold-outline px-7 py-3 text-[0.7rem] uppercase tracking-[0.24em]"
        >
          Back to Shop
        </Link>
      </div>
    );
  }

  const images =
    product.images && product.images.length
      ? product.images
      : product.featuredImage
      ? [product.featuredImage]
      : [];
  const price = selectedVariant?.price || product.minPrice;
  const compareAt = selectedVariant?.compareAtPrice;
  const onSale = compareAt && Number(compareAt.amount) > Number(price.amount);
  const soldOut = !product.availableForSale || !selectedVariant?.availableForSale;
  const hasVariants =
    product.variants.length > 1 ||
    (product.variants[0] && product.variants[0].title !== "Default Title");

  const handleAdd = async () => {
    if (!variantId || soldOut) return;
    setAdding(true);
    try {
      await addItem(variantId, qty);
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <Seo
        title={`${product.title} | Golden Touch Lash & Brow Shop`}
        description={
          (product.description || "").slice(0, 155) ||
          `Shop ${product.title} at Golden Touch Lash & Brow.`
        }
        path={`/shop/${product.handle}`}
        image={product.featuredImage?.url}
        type="product"
        jsonLd={[
          beautySalonSchema(),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: product.title, path: `/shop/${product.handle}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.title,
            description: product.description,
            image: images.map((i) => i.url),
            offers: {
              "@type": "Offer",
              price: price?.amount,
              priceCurrency: price?.currencyCode,
              availability: soldOut
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
            },
          },
        ]}
      />

      <section className="bg-onyx pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <button
            type="button"
            data-testid="back-to-shop"
            onClick={() => navigate("/shop")}
            className="mb-8 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-muted-ivory transition-colors hover:text-gold"
          >
            <ArrowLeft size={15} /> Back to Shop
          </button>

          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Gallery */}
            <div>
              <div className="relative overflow-hidden border border-gold/25 bg-charcoal">
                <span aria-hidden className="pointer-events-none absolute left-2 top-2 z-20 h-5 w-5 border-l-2 border-t-2 border-gold" />
                <span aria-hidden className="pointer-events-none absolute right-2 top-2 z-20 h-5 w-5 border-r-2 border-t-2 border-gold" />
                <span aria-hidden className="pointer-events-none absolute bottom-2 left-2 z-20 h-5 w-5 border-b-2 border-l-2 border-gold" />
                <span aria-hidden className="pointer-events-none absolute bottom-2 right-2 z-20 h-5 w-5 border-b-2 border-r-2 border-gold" />
                {images[activeImg]?.url ? (
                  <img
                    data-testid="product-main-image"
                    src={images[activeImg].url}
                    alt={images[activeImg].altText || product.title}
                    className="aspect-square w-full object-contain p-4"
                  />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center text-gold/40">
                    <ShoppingBag size={56} />
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImg(i)}
                      className={`h-16 w-16 overflow-hidden border transition-all ${
                        i === activeImg ? "border-gold" : "border-gold/20 hover:border-gold/50"
                      }`}
                    >
                      <img src={img.url} alt="" className="h-full w-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col">
              <p className="eyebrow">Golden Touch Shop</p>
              <h1 className="mt-3 font-display text-4xl leading-tight text-ivory md:text-5xl">
                {product.title}
              </h1>

              <div className="mt-4 flex items-baseline gap-3">
                <span className="text-2xl text-gold" data-testid="product-detail-price">
                  {formatMoney(price.amount, price.currencyCode)}
                </span>
                {onSale && (
                  <span className="text-base text-muted-ivory line-through">
                    {formatMoney(compareAt.amount, compareAt.currencyCode)}
                  </span>
                )}
                {onSale && (
                  <span className="bg-gold px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-onyx">
                    Sale
                  </span>
                )}
              </div>

              <div className="gold-divider my-7" />

              {product.description && (
                <p className="text-base leading-relaxed text-muted-ivory" data-testid="product-description">
                  {product.description}
                </p>
              )}

              {hasVariants && (
                <div className="mt-7">
                  <label className="mb-2 block text-[0.65rem] uppercase tracking-[0.24em] text-gold">
                    Options
                  </label>
                  <select
                    data-testid="product-detail-variant"
                    value={variantId || ""}
                    onChange={(e) => setVariantId(e.target.value)}
                    className="w-full appearance-none border border-gold/40 bg-charcoal px-4 py-3 text-sm text-ivory outline-none focus:border-gold"
                  >
                    {product.variants.map((v) => (
                      <option key={v.id} value={v.id} disabled={!v.availableForSale}>
                        {v.title}
                        {!v.availableForSale ? " - Sold out" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Quantity + add */}
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex items-center border border-gold/40">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="flex h-12 w-12 items-center justify-center text-gold hover:bg-gold/10"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center text-ivory" data-testid="detail-qty">{qty}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setQty((q) => q + 1)}
                    className="flex h-12 w-12 items-center justify-center text-gold hover:bg-gold/10"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <button
                  type="button"
                  data-testid="detail-add-to-cart"
                  onClick={handleAdd}
                  disabled={soldOut || adding || busy}
                  className={`inline-flex flex-1 items-center justify-center gap-2 px-8 py-4 text-[0.72rem] uppercase tracking-[0.24em] transition-all disabled:opacity-50 ${
                    added ? "btn-gold" : "btn-gold"
                  }`}
                >
                  {added ? (
                    <>
                      <Check size={16} /> Added to Bag
                    </>
                  ) : soldOut ? (
                    "Sold Out"
                  ) : adding ? (
                    "Adding..."
                  ) : (
                    <>
                      <ShoppingBag size={16} /> Add to Cart
                    </>
                  )}
                </button>
              </div>

              <p className="mt-6 text-xs text-muted-ivory">
                Secure checkout via Shopify. Shipping & taxes calculated at checkout.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
